'use client';

import 'swiper/css';
import 'swiper/css/a11y';
import { useRef, useState } from 'react';
import { A11y, Mousewheel } from 'swiper/modules';
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react';
import SanityImage from '@/components/shared/SanityImage';
import cn from '@/lib/cn';
import { VIEWPORT_SIZES } from '@/lib/constants';
import Button from './Button';

type Entries = {
  _key: string;
  content: {
    author: {
      name: string;
      title: string | null;
    };
    buttonLabel?: string;
    buttonLink?: string;
    image: SanityImageWithAlt;
    quote: string;
  };
  name: string;
}[];

function TabsListSlider({
  activeTabIndex,
  entries,
  setActiveTabIndex,
  swiperInstance,
  tabsContentSwiper,
}: {
  activeTabIndex: number;
  entries: {
    _key: string;
    name: string;
  }[];
  setActiveTabIndex: (index: number) => void;
  swiperInstance: React.RefObject<SwiperClass | null>;
  tabsContentSwiper: React.RefObject<SwiperClass | null>;
}) {
  function onTabClick(index: number) {
    setActiveTabIndex(index);
    swiperInstance.current?.slideTo(index);
    tabsContentSwiper.current?.slideTo(index);
  }

  return (
    <div className="pb-10">
      <Swiper
        className="font-display text-display-2xs !overflow-visible overscroll-contain font-medium"
        centeredSlides
        centeredSlidesBounds
        modules={[A11y, Mousewheel]}
        mousewheel={{
          forceToAxis: true,
        }}
        onSlideChange={(swiper) => {
          if (!tabsContentSwiper.current) return;

          setActiveTabIndex(swiper.realIndex);
          tabsContentSwiper.current.slideTo(swiper.realIndex);
        }}
        onSwiper={(swiper) => {
          swiperInstance.current = swiper;
        }}
        slidesPerView="auto"
        spaceBetween={20}
        breakpoints={{
          [VIEWPORT_SIZES.xl]: {
            slidesPerView: Math.min(entries.length, 7),
            spaceBetween: 16,
          },
        }}
      >
        {entries.map((entry, i) => (
          <SwiperSlide
            key={entry._key}
            className="[@media(max-width:1279px)]:!w-auto"
          >
            <button
              className={cn(
                'after:bg-neutral-10 relative pb-3 text-left uppercase transition-colors after:absolute after:inset-0 after:top-auto after:h-0.5 after:opacity-0 after:transition-opacity xl:w-full xl:border-t xl:border-black/8 xl:py-4 xl:after:-top-px xl:after:bottom-auto',
                activeTabIndex === i
                  ? 'text-black/70 after:opacity-100'
                  : 'text-neutral-40 hover:after:opacity-40',
              )}
              type="button"
              onClick={() => onTabClick(i)}
            >
              {entry.name}
            </button>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

function TabsContentSlider({
  entries,
  quoteSize,
  setActiveTabIndex,
  swiperInstance,
  tabsListSwiper,
}: {
  entries: Entries;
  quoteSize: 'md' | 'lg';
  setActiveTabIndex: (index: number) => void;
  swiperInstance: React.RefObject<SwiperClass | null>;
  tabsListSwiper: React.RefObject<SwiperClass | null>;
}) {
  return (
    <Swiper
      className="!overflow-visible overscroll-contain"
      centeredSlides
      centeredSlidesBounds
      modules={[A11y, Mousewheel]}
      mousewheel={{
        forceToAxis: true,
      }}
      onSlideChange={(swiper) => {
        if (!tabsListSwiper.current) return;

        setActiveTabIndex(swiper.realIndex);
        tabsListSwiper.current.slideTo(swiper.realIndex);
      }}
      onSwiper={(swiper) => {
        swiperInstance.current = swiper;
      }}
      slidesPerView={1.1}
      spaceBetween={20}
    >
      {entries.map((entry) => (
        <SwiperSlide
          key={entry._key}
          className="bg-warm-gray-95 !grid gap-x-14 gap-y-7 rounded-lg p-4 lg:grid-cols-12"
        >
          <SanityImage
            className="aspect-square rounded-md object-cover lg:col-span-5 lg:aspect-[1/1.2]"
            image={entry.content.image}
            sizes={`(min-width: ${VIEWPORT_SIZES.lg}px) 42vw, 100vw`}
          />

          <blockquote className="flex flex-col lg:col-span-7 lg:py-6">
            <cite className="text-md mb-auto font-medium not-italic">
              <span>{entry.content.author.name}</span>{' '}
              {entry.content.author.title && (
                <span
                  className={cn('text-black/60', {
                    block: quoteSize === 'lg',
                  })}
                >
                  {entry.content.author.title}
                </span>
              )}
            </cite>

            <p
              className={cn(
                'font-display text-display-2xs mt-5 mb-auto font-medium uppercase before:content-[open-quote] after:content-[close-quote]',
                {
                  'lg:text-display-md': quoteSize === 'lg',
                },
              )}
            >
              {entry.content.quote}
            </p>

            {entry.content.buttonLabel && entry.content.buttonLink && (
              <p>
                <Button
                  href={entry.content.buttonLink}
                  kind="tertiary"
                >
                  {entry.content.buttonLabel}
                </Button>
              </p>
            )}
          </blockquote>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

export default function TabsSlider({
  className,
  entries,
  quoteSize = 'md',
}: {
  className?: string;
  entries: Entries;
  quoteSize?: 'md' | 'lg';
}) {
  const tabsListSwiper = useRef<SwiperClass>(null);
  const tabsContentSwiper = useRef<SwiperClass>(null);

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  return (
    <div className={className}>
      <TabsListSlider
        entries={entries.map((entry) => ({
          _key: entry._key,
          name: entry.name,
        }))}
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={setActiveTabIndex}
        swiperInstance={tabsListSwiper}
        tabsContentSwiper={tabsContentSwiper}
      />

      <TabsContentSlider
        entries={entries}
        quoteSize={quoteSize}
        setActiveTabIndex={setActiveTabIndex}
        swiperInstance={tabsContentSwiper}
        tabsListSwiper={tabsListSwiper}
      />
    </div>
  );
}
